// Optional transport bundle: same-origin fast-path retrieval (szzt-spec.md §9.8).
//
// Cold retrieval is one manifest fetch plus N resource-blob fetches. A gateway MAY collapse its OWN
// fast path to a single response by streaming every resource block framed as [CID, bytes], so the
// verifier can re-hash each block against its CID. This framing is PRIVATE to a gateway and the
// verifier it serves, and is explicitly NOT a durable or interoperable artifact (§9.8): it is an edge
// latency optimization, not a storage or federation format. It carries NO trust — the consumer
// re-hashes every block exactly as §9.4, and a bundle that omits or corrupts a block fails closed. The
// gateway assembles it ONLY from signed state (the resolved SzztIndex, §9.2); the gateway-
// independent path (§11.3) never uses it and stays manifest + per-resource blobs, so credible exit is
// unaffected.
//
// Framing (v1, private): a 4-byte magic, then blocks streamed to EOF, each a 6-byte header
// (uint16 CID-byte-length, uint32 block-byte-length, both big-endian) followed by the UTF-8 CID and
// the raw block bytes. Any length-prefixed [CID, bytes] encoding satisfies §9.8; this one is chosen
// for a trivial deterministic encode/decode with no dependency.

import type { SzztIndex } from "./types.ts";
import type { VerifyingCache } from "./verify-cache.ts";

/** One framed block: a content CID and the bytes claimed to hash to it (UNVERIFIED until consumed). */
export interface BundleBlock {
  cid: string;
  bytes: Uint8Array;
}

/** The outcome of consuming a bundle into a cache: which blocks verified and which were refused. */
export interface ConsumeResult {
  /** CIDs whose bytes hashed to the claimed CID and are now warm in the cache. */
  accepted: string[];
  /** CIDs whose bytes did NOT hash to the claimed CID — refused, never cached (§9.8 fail closed). */
  rejected: string[];
}

/** Private same-origin media type for the fast path. Non-durable, non-interoperable (§9.8). */
export const BUNDLE_CONTENT_TYPE = "application/vnd.szzt.bundle";

/** Frame magic ("MSB1") so a mis-routed or non-bundle body is rejected outright, not misparsed. */
const MAGIC = Uint8Array.of(0x4d, 0x53, 0x42, 0x31);
const HEADER_BYTES = 6; // uint16 cidLen + uint32 byteLen

/** Thrown when bundle bytes are malformed: bad magic, a truncated frame, or an unframable CID. */
export class BundleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BundleError";
  }
}

/** Frame a list of blocks into the private transport-bundle encoding. Pure; order is preserved. */
export function encodeBundle(blocks: readonly BundleBlock[]): Uint8Array {
  const enc = new TextEncoder();
  const framed = blocks.map((block) => {
    const cidBytes = enc.encode(block.cid);
    if (cidBytes.length > 0xffff) throw new BundleError(`CID too long to frame: ${block.cid}`);
    const header = new Uint8Array(HEADER_BYTES);
    const view = new DataView(header.buffer);
    view.setUint16(0, cidBytes.length, false);
    view.setUint32(2, block.bytes.length, false);
    return { header, cidBytes, bytes: block.bytes };
  });

  const total =
    MAGIC.length +
    framed.reduce((n, f) => n + f.header.length + f.cidBytes.length + f.bytes.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  out.set(MAGIC, off);
  off += MAGIC.length;
  for (const f of framed) {
    out.set(f.header, off);
    off += f.header.length;
    out.set(f.cidBytes, off);
    off += f.cidBytes.length;
    out.set(f.bytes, off);
    off += f.bytes.length;
  }
  return out;
}

/**
 * Decode the private framing back into blocks. Pure and UNVERIFIED — the caller (or {@link consumeBundle})
 * re-hashes each block. Throws {@link BundleError} on bad magic or any truncated frame (fail closed:
 * a partial bundle is refused rather than silently yielding fewer blocks). Block bytes are copied out,
 * so the (potentially large) source buffer is not retained by a warmed cache entry.
 */
export function decodeBundle(data: Uint8Array): BundleBlock[] {
  if (data.length < MAGIC.length || MAGIC.some((b, i) => data[i] !== b)) {
    throw new BundleError("not a site transport bundle (bad magic)");
  }
  const dec = new TextDecoder();
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const blocks: BundleBlock[] = [];
  let off = MAGIC.length;
  while (off < data.length) {
    if (off + HEADER_BYTES > data.length) {
      throw new BundleError("truncated bundle: incomplete frame header");
    }
    const cidLen = view.getUint16(off, false);
    const byteLen = view.getUint32(off + 2, false);
    off += HEADER_BYTES;
    if (off + cidLen > data.length) throw new BundleError("truncated bundle: incomplete CID");
    const cid = dec.decode(data.subarray(off, off + cidLen));
    off += cidLen;
    if (off + byteLen > data.length) throw new BundleError("truncated bundle: incomplete block bytes");
    const bytes = data.slice(off, off + byteLen);
    off += byteLen;
    blocks.push({ cid, bytes });
  }
  return blocks;
}

/**
 * Assemble a transport bundle for a resolved site (the GATEWAY side of §9.8). Frames every
 * resource blob the signed index derived (§9.2) — deduped by CID, since content-addressed bytes shared
 * across paths are one block — fetching + verifying each through the cache so a block that fails to
 * verify is never framed. The root CID is the manifest record, not a transport block, and is excluded.
 * Propagates a fetch/verification failure (a gateway cannot assemble a bundle it cannot verify).
 */
export async function assembleBundle(
  index: SzztIndex,
  cache: VerifyingCache,
): Promise<Uint8Array> {
  const blocks: BundleBlock[] = [];
  for (const loc of index.cidToLocation.values()) {
    if (loc.kind !== "blob") continue; // skip the root CID (manifest bytes, not a resource block)
    const bytes = await cache.getBlob({ did: loc.did, cid: loc.cid, path: loc.path });
    blocks.push({ cid: loc.cid, bytes });
  }
  return encodeBundle(blocks);
}

/**
 * Consume a transport bundle into a verifying cache (the CONSUMER side of §9.8, e.g. the SW fast path).
 * Decodes the private framing, then offers every block to the cache, which re-hashes it against its
 * claimed CID and caches ONLY verified blocks. A corrupt or forged block is rejected — never cached —
 * and the subsequent per-resource serve fetches + verifies it normally (§9.4), so the fast path is a
 * pure optimization that can never serve bad bytes. Throws {@link BundleError} only on malformed
 * framing (a body that isn't a bundle at all); a per-block hash mismatch is a `rejected` entry, not a throw.
 */
export async function consumeBundle(data: Uint8Array, cache: VerifyingCache): Promise<ConsumeResult> {
  const blocks = decodeBundle(data);
  const accepted: string[] = [];
  const rejected: string[] = [];
  for (const block of blocks) {
    if (await cache.offer(block.cid, block.bytes)) accepted.push(block.cid);
    else rejected.push(block.cid);
  }
  return { accepted, rejected };
}
